import type { z } from 'zod'
import { useEnv } from '@library/composables'
import { RtnCode } from '@library/enums'
import type { ApiResponse } from '@library/services/types'

/**
 * 檢查 API 回傳的資料型別是否與前端設定一致
 *
 * @param schema 透過 zod 設定的資料型別規範
 */
export default function apiValidator<T extends z.Schema>(schema: T) {
  const { VITE_SHOW_ZOD_ERROR } = useEnv()

  return function (
    target: object,
    propertyKey: string,
    descriptor: TypedPropertyDescriptor<
      (...args: any[]) => Promise<ApiResponse<z.infer<typeof schema>>>
    >
  ) {
    if (
      !VITE_SHOW_ZOD_ERROR ||
      VITE_SHOW_ZOD_ERROR === 'never' ||
      (VITE_SHOW_ZOD_ERROR === 'query' &&
        new URLSearchParams(window.location.search).get('debug') !== 'true')
    ) {
      return
    }

    if (!descriptor.value) {
      return
    }

    const originalMethod = descriptor.value

    // eslint-disable-next-line sonarjs/no-invariant-returns
    descriptor.value = async function (...args: any[]) {
      const method = originalMethod.apply(this, args)
      const response: ApiResponse = await method

      const result = schema.safeParse(response.data.data)

      if (response.data.rtnCode !== RtnCode.CODE_SUCCESS) {
        return method
      }

      if (!result.success) {
        const data = {
          message: 'API 資料型別驗證錯誤',
          data: response.data,
          response,
          request: response.request,
          apiUrl: response.request?.responseURL,
          result
        }

        console.error({
          source: 'zod',
          type: 'error',
          data: { data: data, issues: result.error.issues }
        })
      }
      return method
    }

    return descriptor
  }
}
